import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(null);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (!result.success) {
      setError(result.error || 'Login failed');
      setLoading(false);
    }
  };

  const handleDemoRegistration = async (e) => {
    e.preventDefault();
    setError('');
    setDemoLoading(true);
    setDemoSuccess(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/auth/register/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: demoEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setDemoSuccess(data);
        setDemoEmail('');
      } else {
        // Handle specific error responses with flexible detail parsing
        let errorMessage = 'Failed to create demo account';

        if (data.detail) {
          // Handle object detail
          if (typeof data.detail === 'object' && data.detail.message) {
            errorMessage = data.detail.message;
          }
          // Handle string detail
          else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }

        // Override with specific messages for certain status codes
        if (response.status === 429) {
          errorMessage = data.detail?.message || 'Rate limit exceeded. Maximum 3 demo account registrations per 24 hours.';
        } else if (response.status === 503) {
          errorMessage = data.detail?.message || 'Demo account limit reached (50/50 active). Please try again later.';
        }

        setError(errorMessage);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Demo registration error:', err);
    } finally {
      setDemoLoading(false);
    }
  };

  const handleUseDemoCredentials = async () => {
    if (demoSuccess) {
      setEmail(demoSuccess.email);
      setPassword(demoSuccess.password);
      setShowDemoForm(false);
      setDemoSuccess(null);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>RAG Document Intelligence</h1>
        <h2>{showDemoForm ? 'Try Demo Account' : 'Login'}</h2>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {demoSuccess && (
          <div className="success-message">
            <h3>Demo Account Created!</h3>
            <div className="demo-credentials">
              <p><strong>Email:</strong> {demoSuccess.email}</p>
              <p><strong>Password:</strong> {demoSuccess.password}</p>
              <p className="demo-expiry">
                <small>Expires: {new Date(demoSuccess.expires_at).toLocaleString()}</small>
              </p>
              <p className="demo-limit">
                <small>Storage Limit: {demoSuccess.storage_limit_mb}MB</small>
              </p>
            </div>
            <div className="demo-actions">
              <button
                className="use-demo-button"
                onClick={handleUseDemoCredentials}
              >
                Use These Credentials to Login
              </button>
              <p className="demo-note">
                <small>Save these credentials! They won't be shown again.</small>
              </p>
            </div>
          </div>
        )}

        {!showDemoForm && !demoSuccess && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        )}

        {showDemoForm && !demoSuccess && (
          <form onSubmit={handleDemoRegistration}>
            <div className="demo-info">
              <p>Create a temporary demo account to try the RAG system:</p>
              <ul>
                <li>Valid for 24 hours</li>
                <li>10MB storage limit</li>
                <li>Full access to all features</li>
              </ul>
            </div>

            <div className="form-group">
              <label htmlFor="demoEmail">Your Email</label>
              <input
                type="email"
                id="demoEmail"
                value={demoEmail}
                onChange={(e) => setDemoEmail(e.target.value)}
                required
                autoFocus
                disabled={demoLoading}
                placeholder="your@email.com"
              />
            </div>

            <button
              type="submit"
              className="demo-button"
              disabled={demoLoading}
            >
              {demoLoading ? 'Creating Account...' : 'Create Demo Account'}
            </button>
          </form>
        )}

        <div className="login-footer">
          {!demoSuccess && (
            <button
              className="toggle-demo-button"
              onClick={() => {
                setShowDemoForm(!showDemoForm);
                setError('');
                setDemoEmail('');
              }}
            >
              {showDemoForm ? '← Back to Login' : 'Try Demo Account →'}
            </button>
          )}
          {!showDemoForm && !demoSuccess && (
            <p className="admin-contact">Contact your administrator for permanent account access</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
